import { apiClient } from '@/shared/lib/api-client';
import type {
  LoginRequest,
  SignupRequest,
  SignupResponse,
  CompleteRegistrationRequest,
  CompleteRegistrationResponse,
  CompleteInvitationRequest,
  CompleteInvitationResponse,
  VerifyTokenResponse,
  AuthResponse,
  User,
  Session,
  ForgotPasswordRequest,
  ResetPasswordRequest,
  ChangePasswordRequest,
} from '../types/auth.types';
import {
  AuthResponseSchema,
  CompleteRegistrationResponseSchema,
  CompleteInvitationResponseSchema,
} from '../types/auth.types';

/**
 * Auth API Service
 *
 * Handles all authentication-related API calls with Zod validation.
 */

/**
 * Sign up with email only (Step 1)
 * Sends verification email to the user
 */
export async function signup(data: SignupRequest): Promise<SignupResponse> {
  const response = await apiClient.post<SignupResponse>('/auth/signup', data);
  return response;
}

/**
 * Verify a token before showing the onboarding wizard
 */
export async function verifyToken(token: string): Promise<VerifyTokenResponse> {
  const response = await apiClient.get<VerifyTokenResponse>(
    `/auth/verify-token/${token}`
  );
  return response;
}

/**
 * Complete registration (Step 2 — onboarding wizard submission)
 * Creates User + Business + StoreHouse and returns tokens
 */
export async function completeRegistration(
  data: CompleteRegistrationRequest
): Promise<CompleteRegistrationResponse> {
  const response = await apiClient.post<CompleteRegistrationResponse>(
    '/auth/complete-registration',
    data
  );
  const validated = CompleteRegistrationResponseSchema.parse(response);
  return validated;
}

/**
 * Login with email and password
 */
export async function login(credentials: LoginRequest): Promise<AuthResponse> {
  const response = await apiClient.post<AuthResponse>(
    '/auth/login',
    credentials
  );

  // Validate response shape
  const validated = AuthResponseSchema.parse(response);

  // Tokens are managed by session store, not stored here
  return validated;
}

/**
 * Logout current session
 */
export async function logout(): Promise<void> {
  await apiClient.post<void>('/auth/logout');

  // Tokens are cleared by session store
}

/**
 * Logout from all devices
 */
export async function logoutAll(): Promise<{ message: string }> {
  const response = await apiClient.post<{ message: string }>(
    '/auth/logout-all'
  );

  // Tokens are cleared by session store
  return response;
}

/**
 * Get current user profile
 */
export async function getCurrentUser(): Promise<User> {
  const response = await apiClient.get<User>('/auth/profile');
  return response;
}

/**
 * Get active sessions
 */
export async function getSessions(): Promise<{
  sessions: Session[];
  count: number;
}> {
  const response = await apiClient.get<{ sessions: Session[]; count: number }>(
    '/auth/sessions'
  );
  return response;
}

/**
 * Revoke a specific session
 */
export async function revokeSession(
  sessionId: string
): Promise<{ message: string }> {
  const response = await apiClient.delete<{ message: string }>(
    `/auth/sessions/${sessionId}`
  );
  return response;
}

/**
 * Request password reset email
 */
export async function forgotPassword(
  data: ForgotPasswordRequest
): Promise<{ message: string }> {
  const response = await apiClient.post<{ message: string }>(
    '/auth/forgot-password',
    data
  );
  return response;
}

/**
 * Reset password with token
 */
export async function resetPassword(
  data: ResetPasswordRequest
): Promise<{ message: string }> {
  const response = await apiClient.post<{ message: string }>(
    '/auth/reset-password',
    { token: data.token, password: data.password }
  );
  return response;
}

/**
 * Complete invitation (invited users — personal details only)
 * Creates User joining existing business
 */
export async function completeInvitation(
  data: CompleteInvitationRequest
): Promise<CompleteInvitationResponse> {
  const response = await apiClient.post<CompleteInvitationResponse>(
    '/auth/complete-invitation',
    data
  );
  const validated = CompleteInvitationResponseSchema.parse(response);
  return validated;
}

/**
 * Get CSRF token
 */
export async function getCsrfToken(): Promise<{
  csrfToken: string;
  headerName?: string;
}> {
  const response = await apiClient.get<{
    csrfToken: string;
    headerName?: string;
  }>('/auth/csrf');
  // CSRF token is already set in cookie by backend
  return response;
}

/**
 * Change password (authenticated user)
 */
export async function changePassword(
  data: ChangePasswordRequest
): Promise<{ message: string }> {
  const response = await apiClient.post<{ message: string }>(
    '/auth/change-password',
    data
  );
  return response;
}

/**
 * Delete account permanently (requires password confirmation)
 */
export async function deleteAccount(
  password: string
): Promise<{ message: string }> {
  const response = await apiClient.post<{ message: string }>(
    '/auth/delete-account',
    { password }
  );
  return response;
}

/**
 * Accept an invitation to join a business (existing user, no business)
 */
export async function acceptInvitation(
  token: string
): Promise<{ message: string; business: any }> {
  const response = await apiClient.post<{ message: string; business: any }>(
    '/auth/accept-invitation',
    { token }
  );
  return response;
}
